import { useTranslation } from 'react-i18next';

import { MessageV2DTO } from 'support/types';
import { MenuButton, MenuButtonNestedOption, MenuButtonOption } from 'components/MenuButton';

import { ActionConfig } from '../types';

export type TestSubmenuItem =
  | {
      kind: 'flat';
      label: string;
      onClick: (message: MessageV2DTO) => unknown;
      analyticsId: string;
      testId?: string;
    }
  | {
      kind: 'nested';
      label: string;
      analyticsId: string;
      subActions: Array<ActionConfig>;
    };

type SecondaryActionsGroups = {
  create: Array<ActionConfig>;
  download: Array<ActionConfig>;
  processing: Array<ActionConfig>;
  test: Array<TestSubmenuItem>;
  resend: Array<ActionConfig>;
};

type SecondaryActionsProps = {
  groups: SecondaryActionsGroups;
  fromDocument: MessageV2DTO;
};

export const SecondaryActions = ({ groups, fromDocument }: SecondaryActionsProps) => {
  const { t } = useTranslation();

  const { create, download, processing, test, resend } = groups;

  if (
    create.length === 0 &&
    download.length === 0 &&
    processing.length === 0 &&
    test.length === 0 &&
    resend.length === 0
  ) {
    return null;
  }

  let isFirstNonEmptyGroupRendered = false;
  const isNewGroup = () => {
    if (!isFirstNonEmptyGroupRendered) {
      isFirstNonEmptyGroupRendered = true;
      return false;
    }
    return true;
  };

  return (
    <MenuButton
      variant="secondary"
      text={t('transactions:show.actionButtons.moreOptions')}
      testId="ActionButtonOptions"
      size="base"
      analyticsId="transactions:secondary_actions"
    >
      {create.length > 0 &&
        create.map((action, index) => (
          <MenuButtonOption
            data-testid={action.testId}
            key={action.analyticsId}
            onClick={() => {
              action.onClick(fromDocument);
            }}
            analyticsId={action.analyticsId}
            testId={action.testId}
            analyticsProperties={action.analyticsProperties}
            newGroup={index === 0 ? isNewGroup() : false}
            disabled={action.disabled?.(fromDocument)}
          >
            <span>
              <span>{t(action.label)}</span>
              {action.description && (
                <span className="block text-sm font-normal text-gray-500">
                  {typeof action.description === 'function' ? action.description(fromDocument) : t(action.description)}
                </span>
              )}
            </span>
          </MenuButtonOption>
        ))}

      {resend.length > 0 && (
        <MenuButtonNestedOption
          label={t('transactions:show.actionButtons.resend')}
          analyticsId="transactions:resend"
          newGroup={isNewGroup()}
        >
          {resend.map((action) => (
            <MenuButtonOption
              key={action.analyticsId}
              onClick={() => action.onClick(fromDocument)}
              analyticsId={action.analyticsId}
              testId={action.testId}
              disabled={action.disabled?.(fromDocument)}
            >
              <span>
                <span>{t(action.label)}</span>
                {action.description && (
                  <span className="block text-sm font-normal text-gray-500">
                    {typeof action.description === 'function'
                      ? action.description(fromDocument)
                      : t(action.description)}
                  </span>
                )}
              </span>
            </MenuButtonOption>
          ))}
        </MenuButtonNestedOption>
      )}

      {processing.length > 0 &&
        processing.map((action, index) => (
          <MenuButtonOption
            data-testid={action.testId}
            key={action.analyticsId}
            onClick={() => {
              action.onClick(fromDocument);
            }}
            analyticsId={action.analyticsId}
            testId={action.testId}
            analyticsProperties={action.analyticsProperties}
            newGroup={index === 0 ? isNewGroup() : false}
            disabled={action.disabled?.(fromDocument)}
          >
            <span>
              <span>{t(action.label)}</span>
              {action.description && (
                <span className="block text-sm font-normal text-gray-500">
                  {typeof action.description === 'function' ? action.description(fromDocument) : t(action.description)}
                </span>
              )}
            </span>
          </MenuButtonOption>
        ))}

      {test.length > 0 && (
        <MenuButtonNestedOption
          label={t('transactions:show.actionButtons.sendTestDocument')}
          analyticsId="transactions:send_test_document"
          newGroup={isNewGroup()}
        >
          {test.map((item) =>
            item.kind === 'flat' ? (
              <MenuButtonOption
                key={item.analyticsId}
                onClick={() => item.onClick(fromDocument)}
                analyticsId={item.analyticsId}
                testId={item.testId}
              >
                {t(item.label)}
              </MenuButtonOption>
            ) : (
              <MenuButtonNestedOption key={item.analyticsId} label={t(item.label)} analyticsId={item.analyticsId}>
                {item.subActions.map((subAction) => (
                  <MenuButtonOption
                    key={subAction.analyticsId}
                    onClick={() => subAction.onClick(fromDocument)}
                    analyticsId={subAction.analyticsId}
                    testId={subAction.testId}
                    disabled={subAction.disabled?.(fromDocument)}
                  >
                    <span>
                      <span>{t(subAction.label)}</span>
                      {subAction.description && (
                        <span className="block text-sm font-normal text-gray-500">
                          {typeof subAction.description === 'function'
                            ? subAction.description(fromDocument)
                            : t(subAction.description)}
                        </span>
                      )}
                    </span>
                  </MenuButtonOption>
                ))}
              </MenuButtonNestedOption>
            ),
          )}
        </MenuButtonNestedOption>
      )}

      {download.length > 0 &&
        download.map((action, index) => (
          <MenuButtonOption
            data-testid={action.testId}
            key={action.analyticsId}
            onClick={() => {
              action.onClick(fromDocument);
            }}
            analyticsId={action.analyticsId}
            testId={action.testId}
            analyticsProperties={action.analyticsProperties}
            newGroup={index === 0 ? isNewGroup() : false}
            disabled={action.disabled?.(fromDocument)}
          >
            <span>
              <span>{t(action.label)}</span>
              {action.description && (
                <span className="block text-sm font-normal text-gray-500">
                  {typeof action.description === 'function' ? action.description(fromDocument) : t(action.description)}
                </span>
              )}
            </span>
          </MenuButtonOption>
        ))}
    </MenuButton>
  );
};
